// =============================================================================
// Faithful HTML reproductions of the legacy IMS ActiveReports documents:
//   - Estimate_Report  (+ EstimateItems_Report, EstimatePictures_Report)
//   - Invoice_report   (job invoice, + InvoiceItems_Report)
//
// Layout/labels/letterhead are ported from
//   thomsons-ims\...\Reports - Actual Reports\Estimate_Report.Designer.vb
//   thomsons-ims\...\Reports - Actual Reports\Invoice_Report.vb
// so the printed output matches the originals (Times New Roman, centered
// "Thomson's / Landscaping and Garden Center" letterhead, items grouped by
// type with per-type subtotals, the GUARANTEE box, and the totals block).
// =============================================================================
import { currency, customerLabel } from './format'
import { REPORT_LOGO_LEFT, REPORT_LOGO_RIGHT } from './reportAssets'
import type { Estimate, EstimateItem, Bill, BillItem, Customer, InventoryPicture } from '../types'

// Item type print order — faithful to EstimateItems_Report/InvoiceItems_Report
// typeOrder = {"Plants","Materials","Labor","Rental"}. Items whose type is not in
// this list get OrderBy index -1 in the legacy, i.e. they sort BEFORE these.
const TYPE_ORDER = ['Plants', 'Materials', 'Labor', 'Rental']

// Legacy GUARANTEE / quote text (Estimate_Report.Designer.vb -> Me.Guarantee.Text;
// editable in legacy via frmEstimateHeader -> tblUtility.QuoteText). The new app
// stores an override in app_settings (key 'estimate_quote_text'); this is the
// fallback when none is set.
export const DEFAULT_GUARANTEE =
  'GUARANTEE— All plants will be true to name, insect and disease free and if paid ' +
  'in full within 30 days will be guaranteed for one year, except in cases of ' +
  'vandalism, acts of God or homeowner neglect.'

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function bucket(t: string | null | undefined): string {
  return t ?? 'Other'
}

function fmtPct(n: number | null | undefined): string {
  return `${(Number(n) || 0).toFixed(2)}%`
}

function longDate(d: string | null | undefined): string {
  if (!d) return ''
  const dt = new Date(d)
  if (isNaN(dt.getTime())) return String(d)
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

// Shared CSS appended into the print window (Times New Roman, letterhead, etc.).
export const REPORT_CSS = `
  .rpt { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 12pt; }
  .rpt table { border-collapse: collapse; width: 100%; margin: 0; font-size: 12pt; }
  .rpt td, .rpt th { border: none; padding: 1px 4px; }
  .rpt .hdr td { vertical-align: top; }
  .rpt .lh { text-align: center; line-height: 1.1; }
  .rpt .lh .nm { font-size: 28pt; font-weight: bold; }
  .rpt .lh .sub { font-size: 18pt; font-weight: bold; }
  .rpt .lh .addr { font-size: 12pt; font-weight: bold; margin-top: 4px; }
  .rpt .meta { text-align: right; font-weight: bold; font-size: 11pt; }
  .rpt .meta span { display: inline-block; min-width: 78px; text-align: left; }
  .rpt .logo-l { width: 1.44in; height: auto; }
  .rpt .logo-r { width: 1.18in; height: auto; display: block; margin: 10px 0 0 auto; }
  .rpt .rule { border: none; border-top: 2px solid #000; margin: 6px 0; }
  .rpt .cust { font-weight: bold; }
  .rpt .cust .nm { font-size: 12pt; }
  .rpt .cust .ph { text-align: right; font-weight: bold; }
  .rpt .ci { font-style: italic; font-weight: bold; }
  .rpt table.items { margin-top: 8px; }
  .rpt table.items th { font-size: 13pt; font-weight: bold; border-bottom: 2px solid #000; padding-bottom: 2px; }
  .rpt table.items td { font-weight: bold; }
  .rpt table.items td.r, .rpt table.items th.r { text-align: right; }
  .rpt tr.grp td { font-weight: bold; border: 1px solid #000; background: #f0f0f0; padding: 2px 6px; margin-top: 6px; }
  .rpt tr.sub td { border-top: 1px solid #000; font-weight: bold; }
  .rpt .foot { margin-top: 14px; }
  .rpt .foot td { vertical-align: top; }
  .rpt .guar { border: 1px solid #000; padding: 6px; font-family: 'Microsoft Sans Serif', Arial, sans-serif; font-size: 8pt; font-weight: bold; width: 4.25in; }
  .rpt table.tot { width: auto; float: right; }
  .rpt table.tot td { font-weight: bold; text-align: right; padding: 1px 6px; }
  .rpt table.tot td.lbl { text-align: right; }
  .rpt table.tot tr.grand td { font-size: 16pt; border-top: 2px solid #000; }
  .rpt .disc-box { border: 1px solid #000; padding: 4px 8px; margin-top: 8px; display: inline-block; font-size: 10pt; }
  .rpt .disc-box .dh { font-weight: bold; text-decoration: underline; }
  .rpt .pics { page-break-before: always; }
  .rpt .pics h2 { font-size: 18pt; text-align: center; margin-bottom: 12px; }
  .rpt .pic { display: flex; gap: 16px; align-items: flex-start; margin: 10px 0; padding-bottom: 10px; border-bottom: 1px solid #ccc; page-break-inside: avoid; }
  .rpt .pic img { width: 2.2in; height: auto; border: 1px solid #999; }
  .rpt .pic .info .nm { font-size: 13pt; font-weight: bold; }
  .rpt .pic .info .care { font-size: 10pt; margin-top: 4px; }
  .rpt .pic .info .desc { font-size: 10pt; margin-top: 6px; }
  .rpt .quote-letter { font-size: 12pt; font-weight: bold; margin: 14px 0; line-height: 1.3; white-space: normal; }
`

function letterhead(): string {
  return `<div class="lh">
    <div class="nm">Thomson's</div>
    <div class="sub">Landscaping and Garden Center</div>
    <div class="addr">26130 State Route 7<br/>Marietta, OH&nbsp; 45750-5113<br/>Phone:&nbsp; 740-374-9353</div>
  </div>`
}

function customerBlock(opts: {
  name: string; company?: string | null; address?: string | null; cityLine?: string | null;
  phoneHome?: string | null; phoneWork?: string | null; custInfo?: string | null; comments?: string | null;
}): string {
  const left = [
    `<div class="nm">${esc(opts.name)}</div>`,
    opts.company ? `<div>${esc(opts.company)}</div>` : '',
    opts.address ? `<div>${esc(opts.address)}</div>` : '',
    opts.cityLine ? `<div>${esc(opts.cityLine)}</div>` : '',
  ].join('')
  const right = [
    opts.phoneHome ? `<div>${esc(opts.phoneHome)}</div>` : '',
    opts.phoneWork ? `<div>${esc(opts.phoneWork)}</div>` : '',
  ].join('')
  const ci = opts.custInfo ? `<div class="ci">${esc(opts.custInfo)}</div>` : ''
  const cm = opts.comments ? `<div class="ci">${esc(opts.comments)}</div>` : ''
  return `<table class="cust"><tr>
      <td style="width:65%">${left}</td>
      <td class="ph" style="width:35%">${right}</td>
    </tr></table>${ci}${cm}`
}

// Items table: grouped by type, per-type subtotal. Labor rows hide qty/size/price
// (faithful to EstimateItems_Report.Detail_Format).
function itemsTable(
  items: Array<{ item_name: string | null; item_size: string | null; item_type: string | null;
    qty: number | null; price: number | null; total: number | null; discount_dollar: number | null }>,
): string {
  // "Hidden Cost" lines are never shown to the customer (legacy
  // EstimateItems_Report Detail.Visible=False / InvoiceItems_Report
  // Item_Type <> "Hidden Cost"). They remain in the grand total, which is
  // summed from the full item set by the caller.
  const shown = items.filter(i => (i.item_type ?? '') !== 'Hidden Cost')
  const types = [...new Set(shown.map(i => bucket(i.item_type)))]
  const present = [
    ...types.filter(t => !TYPE_ORDER.includes(t)),
    ...TYPE_ORDER.filter(t => types.includes(t)),
  ]
  const head = `<thead><tr>
      <th style="width:8%">Qty</th>
      <th style="width:12%">Size</th>
      <th>Description</th>
      <th class="r" style="width:14%">Unit Price</th>
      <th class="r" style="width:10%">Disc</th>
      <th class="r" style="width:14%">Total</th>
    </tr></thead>`
  const body = present.map(t => {
    const lines = shown.filter(i => bucket(i.item_type) === t)
    const sub = lines.reduce((s, i) => s + (i.total ?? 0), 0)
    const isLabor = t === 'Labor'
    const rows = lines.map(i => {
      const disc = (i.discount_dollar ?? 0) > 0 ? currency(i.discount_dollar) : ''
      return `<tr>
        <td>${isLabor ? '' : esc(i.qty ?? '')}</td>
        <td>${isLabor ? '' : esc(i.item_size ?? '')}</td>
        <td>${esc(i.item_name ?? '')}</td>
        <td class="r">${isLabor ? '' : currency(i.price)}</td>
        <td class="r">${disc}</td>
        <td class="r">${currency(i.total)}</td>
      </tr>`
    }).join('')
    return `<tr class="grp"><td colspan="6">${esc(t)}</td></tr>${rows}` +
      `<tr class="sub"><td colspan="5" class="r">Total:</td><td class="r">${currency(sub)}</td></tr>`
  }).join('')
  return `<table class="items">${head}<tbody>${body}</tbody></table>`
}

// Russ proposal item list — Qty / Size / Description only (NO prices), grouped by
// type. Labor rows hide Qty/Size; Hidden Cost excluded. Faithful to
// EstimateItems_Report_Russ, whose Detail binds only Item_Qty/Item_Size/Item_Name.
function itemsTableNoPrice(
  items: Array<{ item_name: string | null; item_size: string | null; item_type: string | null; qty: number | null }>,
): string {
  const shown = items.filter(i => (i.item_type ?? '') !== 'Hidden Cost')
  const types = [...new Set(shown.map(i => bucket(i.item_type)))]
  const present = [
    ...types.filter(t => !TYPE_ORDER.includes(t)),
    ...TYPE_ORDER.filter(t => types.includes(t)),
  ]
  const head = `<thead><tr>
      <th style="width:10%">Qty</th>
      <th style="width:16%">Size</th>
      <th>Description</th>
    </tr></thead>`
  const body = present.map(t => {
    const lines = shown.filter(i => bucket(i.item_type) === t)
    const isLabor = t === 'Labor'
    const rows = lines.map(i => `<tr>
        <td>${isLabor ? '' : esc(i.qty ?? '')}</td>
        <td>${isLabor ? '' : esc(i.item_size ?? '')}</td>
        <td>${esc(i.item_name ?? '')}</td>
      </tr>`).join('')
    return `<tr class="grp"><td colspan="3">${esc(t)}</td></tr>${rows}`
  }).join('')
  return `<table class="items">${head}<tbody>${body}</tbody></table>`
}

function discountBox(rows: Array<{ label: string; value: string }>): string {
  const present = rows.filter(r => r.value)
  if (present.length === 0) return ''
  const cells = present.map(r => `<tr><td class="dh">${esc(r.label)}</td><td style="text-align:right">${esc(r.value)}</td></tr>`).join('')
  return `<div class="disc-box"><table>${cells}</table></div>`
}

// Customer site-visit photos attached to the estimate (chosen by the designer).
// Rendered on their own page after the plant pictures, larger and captioned.
function mediaPage(photos: Array<{ url: string; title?: string | null }>): string {
  if (!photos.length) return ''
  const cards = photos.map(p => `<div class="pic">
      <img src="${esc(p.url)}" alt="${esc(p.title ?? '')}"/>
      <div class="info">${p.title ? `<div class="nm">${esc(p.title)}</div>` : ''}</div>
    </div>`).join('')
  return `<div class="pics"><h2>Site Visit Photos</h2>${cards}</div>`
}

// Faithful to EstimatePictures_Report: image + "Plant Name:" (inventory
// description) + "Description:" (the picture's description). No care fields
// (Height/Bloom/Exposure/Fruit) and no page heading — the legacy report shows
// only those two labelled values beside the photo.
function picturesPage(pics: InventoryPicture[]): string {
  if (pics.length === 0) return ''
  const cards = pics.map(p => {
    const img = p.image_url ? `<img src="${esc(p.image_url)}" alt="${esc(p.name)}"/>` : ''
    return `<div class="pic">
      ${img}
      <div class="info">
        <div class="nm">Plant Name:&nbsp; ${esc(p.name)}</div>
        ${p.description ? `<div class="desc">Description:&nbsp; ${esc(p.description)}</div>` : ''}
      </div>
    </div>`
  }).join('')
  return `<div class="pics">${cards}</div>`
}

// --- Public builders ---------------------------------------------------------

export function estimateReportHtml(args: {
  estimate: Estimate; items: EstimateItem[]; customer: Customer | null;
  designerName?: string | null; russ?: boolean; pictures?: InventoryPicture[];
  guaranteeText?: string | null;
  mediaPhotos?: Array<{ url: string; title?: string | null }>;
}): string {
  const { estimate, items, customer } = args
  const cityLine = customer
    ? [customer.city, customer.state].filter(Boolean).join(', ') + (customer.zip ? '  ' + customer.zip : '')
    : ''
  const subtotal = items.reduce((s, i) => s + (i.total ?? 0), 0)
  const taxAmt = estimate.sales_tax_amount ?? 0
  const grand = estimate.estimate_total ?? subtotal + taxAmt

  const meta = `<div class="meta">
      <div><span>Estimate #:</span> ${esc(estimate.bill_code ?? estimate.legacy_id ?? '')}</div>
      <div><span>Date:</span> ${esc(longDate(estimate.estimate_date))}</div>
      ${args.designerName ? `<div><span>Designer:</span> ${esc(args.designerName)}</div>` : ''}
    </div>`

  const header = `<table class="hdr"><tr>
      <td style="width:25%"><img class="logo-l" src="${REPORT_LOGO_LEFT}" alt=""/></td>
      <td style="width:50%">${letterhead()}</td>
      <td style="width:25%">${meta}<img class="logo-r" src="${REPORT_LOGO_RIGHT}" alt=""/></td>
    </tr></table><hr class="rule"/>`

  const cust = customerBlock({
    name: customer ? customerLabel(customer) : '',
    company: customer?.company_name,
    address: customer?.address,
    cityLine,
    phoneHome: customer?.phone_home,
    phoneWork: customer?.phone_work,
    custInfo: estimate.customer_info,
    comments: estimate.comments,
  })

  const discs = discountBox([
    { label: 'Plant Discount', value: (estimate.plant_discount_d ?? 0) > 0 ? currency(estimate.plant_discount_d) : '' },
    { label: 'Material Discount', value: (estimate.material_discount_d ?? 0) > 0 ? currency(estimate.material_discount_d) : '' },
    { label: 'Labor Discount', value: (estimate.labor_discount_d ?? 0) > 0 ? currency(estimate.labor_discount_d) : '' },
  ])

  const totals = `<table class="tot">
      <tr><td class="lbl">Subtotal:</td><td>${currency(subtotal)}</td></tr>
      <tr><td class="lbl">Sales Tax:</td><td>${currency(taxAmt)}</td></tr>
      <tr class="grand"><td class="lbl">Estimate Total:</td><td>${currency(grand)}</td></tr>
    </table>`

  // The GUARANTEE box is the static guarantee on BOTH the standard and Russ
  // reports (legacy Estimate_Report Me.Guarantee is a fixed label). The editable
  // tblUtility.QuoteText — passed in here as guaranteeText — is the Russ cover
  // letter, NOT the guarantee.
  const foot = `<div class="foot"><table><tr>
      <td style="width:55%"><div class="guar">${esc(DEFAULT_GUARANTEE)}</div>${discs}</td>
      <td style="width:45%">${totals}</td>
    </tr></table><div style="clear:both"></div></div>`

  const pics = picturesPage(args.pictures ?? []) + mediaPage(args.mediaPhotos ?? [])

  // Russ Version = customer proposal: the QuoteText cover letter (with [LSQ] =
  // lump-sum quote, [TX] = tax, [TOTAL] substituted) followed by a NO-PRICE item
  // list. Faithful to Estimate_Report_Russ.ReportHeader_Format.
  if (args.russ) {
    const raw = (args.guaranteeText && args.guaranteeText.trim()) ? args.guaranteeText : ''
    const letter = raw
      ? `<div class="quote-letter">${esc(raw)
          .replace(/\[LSQ\]/g, currency(subtotal))
          .replace(/\[TX\]/g, currency(taxAmt))
          .replace(/\[TOTAL\]/g, currency(subtotal + taxAmt))
          .replace(/\r?\n/g, '<br/>')}</div>`
      : ''
    return `<div class="rpt">${header}${cust}${letter}${itemsTableNoPrice(items)}${foot}${pics}</div>`
  }

  return `<div class="rpt">${header}${cust}${itemsTable(items)}${foot}${pics}</div>`
}

export function invoiceReportHtml(args: {
  bill: Bill; items: BillItem[]; customer: Customer | null; pictures?: InventoryPicture[];
  // Contractor copy: Qty/Size/Description only, NO prices/totals/discounts
  // (mirrors the estimate "Russ Version"). Used for the field/contractor sheet.
  noPrices?: boolean;
}): string {
  const { bill, items, customer, noPrices } = args
  // Bill carries a customer snapshot (used when NonCustomer); fall back to the
  // linked customer record — faithful to Invoice_report.ReportStart.
  const name = bill.customer_name || (customer ? customerLabel(customer) : '')
  const company = bill.customer_name ? null : customer?.company_name
  const address = bill.customer_address ?? customer?.address ?? null
  const cityLine = [bill.customer_city ?? customer?.city, bill.customer_state ?? customer?.state]
    .filter(Boolean).join(', ') + ((bill.customer_zip ?? customer?.zip) ? '  ' + (bill.customer_zip ?? customer?.zip) : '')
  const phoneHome = bill.customer_phone1 ?? customer?.phone_home ?? null
  const phoneWork = bill.customer_phone2 ?? customer?.phone_work ?? null

  const subtotal = items.reduce((s, i) => s + (i.total ?? 0), 0)
  const taxAmt = bill.sales_tax_amount ?? 0
  const grand = bill.invoice_total ?? subtotal + taxAmt
  const deposit = bill.deposit ?? 0
  const balance = bill.remaining_balance ?? grand - deposit
  const wholeDisc = bill.disc_whole_invoice ?? 0

  const meta = `<div class="meta">
      <div><span>Invoice #:</span> ${esc(bill.bill_code ?? bill.legacy_bill_id ?? '')}</div>
      <div><span>Date:</span> ${esc(longDate(bill.bill_date))}</div>
    </div>`

  const header = `<table class="hdr"><tr>
      <td style="width:25%"><img class="logo-l" src="${REPORT_LOGO_LEFT}" alt=""/></td>
      <td style="width:50%">${letterhead()}</td>
      <td style="width:25%">${meta}<img class="logo-r" src="${REPORT_LOGO_RIGHT}" alt=""/></td>
    </tr></table><hr class="rule"/>`

  const cust = customerBlock({
    name, company, address, cityLine, phoneHome, phoneWork,
    comments: bill.comments ?? bill.comment,
  })

  // Per-type discounts — legacy Invoice_report shows the discount PERCENT and the
  // computed dollar amount per type, hidden when the percent is 0
  // (Invoice_Report.vb:72-99 -> lblPP "Plants Discount Percentage:" etc.).
  const discRow = (label: string, p: number | null, d: number | null) => {
    const pp = Number(p) || 0, dd = Number(d) || 0
    if (pp <= 0 && dd <= 0) return { label, value: '' }
    return { label, value: pp > 0 ? `${fmtPct(pp)}${dd > 0 ? ` (${currency(dd)})` : ''}` : currency(dd) }
  }
  const discs = discountBox([
    discRow('Plant Discount', bill.inv_plant_discount_p, bill.inv_plant_discount_d),
    discRow('Material Discount', bill.inv_material_discount_p, bill.inv_material_discount_d),
    discRow('Labor Discount', bill.inv_labor_discount_p, bill.inv_labor_discount_d),
  ])

  const totRows = [
    `<tr><td class="lbl">Subtotal:</td><td>${currency(subtotal)}</td></tr>`,
    wholeDisc > 0 ? `<tr><td class="lbl">Invoice Discount:</td><td>-${currency(wholeDisc)}</td></tr>` : '',
    `<tr><td class="lbl">Sales Tax${(bill.sales_tax_percent ?? 0) > 0 ? ` (${fmtPct(bill.sales_tax_percent)})` : ''}:</td><td>${currency(taxAmt)}</td></tr>`,
    `<tr class="grand"><td class="lbl">Invoice Total:</td><td>${currency(grand)}</td></tr>`,
    deposit > 0 ? `<tr><td class="lbl">Deposit:</td><td>${currency(deposit)}</td></tr>` : '',
    deposit > 0 ? `<tr><td class="lbl">Balance:</td><td>${currency(balance)}</td></tr>` : '',
  ].filter(Boolean).join('')
  const totals = `<table class="tot">${totRows}</table>`

  const depInfo = (deposit > 0 && bill.deposit_info)
    ? `<div class="ci" style="margin-top:6px">${esc(bill.deposit_info)}</div>` : ''

  const foot = `<div class="foot"><table><tr>
      <td style="width:55%">${discs}${depInfo}</td>
      <td style="width:45%">${totals}</td>
    </tr></table><div style="clear:both"></div></div>`

  const pics = picturesPage(args.pictures ?? [])

  // Contractor copy omits the prices on items and the entire totals/discount
  // footer — only the customer block and the no-price item list remain.
  if (noPrices) {
    return `<div class="rpt">${header}${cust}${itemsTableNoPrice(items)}${pics}</div>`
  }

  return `<div class="rpt">${header}${cust}${itemsTable(items)}${foot}${pics}</div>`
}
